import {
  TypeDict,
  CompanyDict,
  CountryDict,
  ProjectNDepartments,
  RequestCount,
} from 'types';
import { OptionExtended, Option, OptionNum } from '@Components/types/options';
import { TaskCount } from '@Types/common/TaskCount';
import { RequestTypeCodes } from '@Enum/index';
import {
  CostCenter,
  Organization,
  Project,
} from '@Types/api/common/dictionary';
import { StoreOverheadResponse } from '@Types/api/form/materialRequisition/MaterialStoreOverheadResponse';
import { Currency } from '@Types/api/common/dictionary/Currency';

export const transformDictToOption = (
  dictionary: TypeDict[],
  byValue: number
): Option => {
  const findedOption = dictionary.find((dict) => dict.codeNum === byValue)!;
  return {
    label: findedOption.codeDesc!,
    value: String(findedOption.codeNum),
  };
};

export const transformCurrenciesToOptions = (
  currencies: Currency[]
): Option[] => {
  return currencies.map((item) => ({
    label: item.currencyCode,
    value: String(item.currencyId),
  }));
};

export const transformStoreOverheadToOption = (
  storeOverhead: StoreOverheadResponse
): Option[] => {
  return [
    {
      label: storeOverhead?.storeName,
      value: String(storeOverhead?.storeId),
    },
  ];
};

export const transformCostCenterDictToOptions = (
  dictionary: CostCenter[]
): Option[] => {
  return dictionary.map((dictItem) => ({
    label: dictItem.name,
    value: String(dictItem.id),
  }));
};

export const transformCountriesDictToOption = (
  dictionary: CountryDict[],
  byValue: number
): Option => {
  const findedOption = dictionary.find((dict) => dict.id === byValue)!;
  return {
    label: findedOption.countryName!,
    value: String(findedOption.id),
  };
};

export const transformDictToOptions = (dictionary: TypeDict[]): Option[] => {
  return dictionary.map((dictItem) => ({
    label: dictItem.codeDesc,
    value: String(dictItem.codeNum),
  }));
};

export const prepareRequestTypeOptions = (
  dictionary: TypeDict[],
  requestsCount: RequestCount[]
): OptionExtended[] => {
  return dictionary.map((dictItem) => ({
    label: dictItem.codeDesc,
    value: String(dictItem.codeNum),
    description:
      requestsCount?.find((item) => item.type === dictItem.codeNum)?.quantity ||
      0,
  }));
};

// temp solution, will be updated once tasks count contain request type codes
const getCounter = (dictItem: TypeDict, tasksCount: TaskCount[]): number => {
  if (dictItem.codeNum === RequestTypeCodes.ATTENDANCE_REQUEST) {
    return (
      tasksCount.find((item) => item.requestType === 'Attendance')?.quantity ||
      0
    );
  }

  if (dictItem.codeNum === RequestTypeCodes.RESUMPTION_REQUEST) {
    return (
      tasksCount.find((item) => item.requestType === 'Resumption')?.quantity ||
      0
    );
  }
  if (dictItem.codeNum === RequestTypeCodes.IT_REQUISITION) {
    return (
      tasksCount.find((item) => item.requestType === 'IT Requisition')
        ?.quantity || 0
    );
  }
  return (
    tasksCount.find((item) => item.requestType === dictItem.codeDesc)
      ?.quantity || 0
  );
};

export const prepareApprovalReqTypeOptions = (
  dictionary: TypeDict[],
  tasksCount: TaskCount[]
): OptionExtended[] => {
  return dictionary.map((dictItem) => ({
    label: dictItem.codeDesc,
    value: String(dictItem.codeNum),
    description: getCounter(dictItem, tasksCount),
  }));
};

export const transformDictToOptionsNum = (
  dictionary: TypeDict[]
): OptionNum[] => {
  return dictionary.map((dictItem) => ({
    label: dictItem.codeDesc,
    value: dictItem.codeNum,
  }));
};

export const transformCountriesDictToOptions = (
  dictionary: CountryDict[]
): Option[] => {
  return dictionary.map((country) => ({
    label: country.countryName,
    value: String(country.id),
  }));
};

export const transformCompaniesDictToOptions = (
  dictionary: CompanyDict[]
): Option[] => {
  return dictionary.map((company) => ({
    label: company.compName,
    value: String(company.compId),
  }));
};

export const transformCompaniesDictToOptionsNum = (
  dictionary: CompanyDict[]
): OptionNum[] => {
  return dictionary.map((company) => ({
    label: company.compCode,
    value: company.compId,
  }));
};

export const transformDeptProjDictToOptions = (
  dictionary: ProjectNDepartments[]
): OptionNum[] => {
  return dictionary.map((deptProj) => ({
    label: deptProj.projDeptName,
    value: deptProj.projDeptID,
  }));
};

export const transformFlagsToOption = (flag: boolean): Option => {
  return {
    label: flag ? 'Yes' : 'No',
    value: String(Number(flag)),
  };
};

export const transformFlagsToOptions = (): Option[] => {
  return [
    {
      label: 'Yes',
      value: '1',
    },
    {
      label: 'No',
      value: '0',
    },
  ];
};

export const transformBooleanValueToLabel = (
  value: 'Yes' | 'No'
): '1' | '0' => {
  if (value === 'Yes') {
    return '1';
  }

  return '0';
};

export const transformBooleanLabelToValue = (value: string): 1 | 0 => {
  if (value.toLowerCase() === 'yes') {
    return 1;
  }

  return 0;
};

export const transformOrganizationsToOptions = (
  organizations: Organization[]
): Option[] => {
  return organizations.map((org) => ({
    label: org.borgName,
    value: String(org.borgId),
  }));
};

export const transformProjectsToOptions = (projects: Project[]): Option[] => {
  return projects.map((project) => ({
    label: project.name,
    value: String(project.id),
  }));
};

export const transformProjectsToLabelOptions = (
  projects: Project[]
): Option[] => {
  return projects.map((project) => ({
    label: project.name,
    value: project.name,
  }));
};
